import { useEffect, useState } from "react";
import { api, withToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useContent, DEFAULTS } from "@/context/ContentContext";
import { toast } from "sonner";
import { Save, Plus, Trash2, RotateCcw, Loader2 } from "lucide-react";

const TOKEN_KEY = "mirabella_admin_token";

function handleAuthError(err) {
  if (err?.response?.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    toast.error("Session expired — please sign in again.");
    window.location.reload();
    return true;
  }
  return false;
}

const inputClass =
  "mt-2 h-11 bg-white border-[#5B6E4A]/25 focus-visible:ring-[#C65D47]";
const textareaClass =
  "mt-2 bg-white border-[#5B6E4A]/25 focus-visible:ring-[#C65D47] text-[15px]";

export default function ContentEditor({ token }) {
  const { content, refresh } = useContent();
  const [draft, setDraft] = useState(content);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Sync draft whenever the upstream content changes (e.g. after refresh).
  useEffect(() => {
    setDraft(content);
    setDirty(false);
  }, [content]);

  const update = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", draft, withToken(token));
      await refresh();
      toast.success("Saved. The website is updated.");
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const resetSection = (key) => {
    if (!window.confirm("Restore default text for this section?")) return;
    update({ [key]: DEFAULTS[key] });
  };

  return (
    <div data-testid="content-editor" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif-display text-2xl text-[#232E26]">
            Website text
          </h2>
          <p className="text-sm text-[#5B6E4A] mt-1">
            Edit any text on the landing page. Changes go live the moment you
            save.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <span className="text-xs text-[#C65D47] uppercase tracking-[0.2em]">
              Unsaved changes
            </span>
          )}
          <Button
            data-testid="content-save"
            onClick={save}
            disabled={saving || !dirty}
            className="bg-[#C65D47] hover:bg-[#A84A35] text-white"
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin mr-2" />
            ) : (
              <Save size={14} className="mr-2" />
            )}
            Save changes
          </Button>
        </div>
      </div>

      <Accordion
        type="multiple"
        defaultValue={["property"]}
        className="bg-white border border-[#EAE0D5]"
      >
        {/* ---------------- Property ---------------- */}
        <AccordionItem value="property" className="border-b border-[#EAE0D5]">
          <AccordionTrigger
            data-testid="acc-property"
            className="px-6 hover:no-underline"
          >
            <span className="font-serif-display text-lg text-[#232E26]">
              Hero &amp; intro (top of page)
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6 space-y-5">
            <Field
              id="location"
              label="Location line (above title)"
              value={draft.location}
              onChange={(v) => update({ location: v })}
            />
            <Field
              id="shortDescription"
              label="Hero short description"
              value={draft.shortDescription}
              onChange={(v) => update({ shortDescription: v })}
              multiline
              rows={3}
            />
            <Field
              id="longAbout"
              label="About section (long story)"
              value={draft.longAbout}
              onChange={(v) => update({ longAbout: v })}
              multiline
              rows={8}
              help="Use blank lines to create paragraphs."
            />
            <ResetButton onClick={() => {
              update({
                location: DEFAULTS.location,
                shortDescription: DEFAULTS.shortDescription,
                longAbout: DEFAULTS.longAbout,
              });
            }} />
          </AccordionContent>
        </AccordionItem>

        {/* ---------------- Highlights ---------------- */}
        <AccordionItem value="highlights" className="border-b border-[#EAE0D5]">
          <AccordionTrigger
            data-testid="acc-highlights"
            className="px-6 hover:no-underline"
          >
            <span className="font-serif-display text-lg text-[#232E26]">
              Why you&apos;ll love it ({(draft.highlights || []).length} cards)
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div className="grid md:grid-cols-2 gap-5">
              {(draft.highlights || []).map((h, i) => (
                <div
                  key={i}
                  data-testid={`highlight-edit-${i}`}
                  className="border border-[#EAE0D5] p-5"
                >
                  <p className="overline">Card 0{i + 1}</p>
                  <Field
                    id={`hl-title-${i}`}
                    label="Title"
                    value={h.title}
                    onChange={(v) => {
                      const next = [...draft.highlights];
                      next[i] = { ...next[i], title: v };
                      update({ highlights: next });
                    }}
                  />
                  <Field
                    id={`hl-body-${i}`}
                    label="Body"
                    value={h.body}
                    onChange={(v) => {
                      const next = [...draft.highlights];
                      next[i] = { ...next[i], body: v };
                      update({ highlights: next });
                    }}
                    multiline
                    rows={3}
                  />
                </div>
              ))}
            </div>
            <ResetButton onClick={() => resetSection("highlights")} />
          </AccordionContent>
        </AccordionItem>

        {/* ---------------- Hosts ---------------- */}
        <AccordionItem value="owners" className="border-b border-[#EAE0D5]">
          <AccordionTrigger
            data-testid="acc-owners"
            className="px-6 hover:no-underline"
          >
            <span className="font-serif-display text-lg text-[#232E26]">
              Hosts (about you)
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6 space-y-5">
            <Field
              id="ownersTitle"
              label="Section label (e.g. From your hosts)"
              value={draft.ownersTitle}
              onChange={(v) => update({ ownersTitle: v })}
            />
            <Field
              id="ownersBody"
              label="Your bio"
              value={draft.ownersBody}
              onChange={(v) => update({ ownersBody: v })}
              multiline
              rows={10}
              help="Use blank lines to create paragraphs."
            />
            <ResetButton onClick={() => {
              update({
                ownersTitle: DEFAULTS.ownersTitle,
                ownersBody: DEFAULTS.ownersBody,
              });
            }} />
          </AccordionContent>
        </AccordionItem>

        {/* ---------------- Reviews ---------------- */}
        <AccordionItem value="reviews" className="border-b border-[#EAE0D5]">
          <AccordionTrigger
            data-testid="acc-reviews"
            className="px-6 hover:no-underline"
          >
            <span className="font-serif-display text-lg text-[#232E26]">
              Guest reviews ({(draft.reviews || []).length})
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div className="space-y-4">
              {(draft.reviews || []).map((r, i) => (
                <div
                  key={i}
                  data-testid={`review-edit-${i}`}
                  className="border border-[#EAE0D5] p-5"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <p className="overline">Review #{i + 1}</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid={`review-delete-${i}`}
                      onClick={() => {
                        const next = [...draft.reviews];
                        next.splice(i, 1);
                        update({ reviews: next });
                      }}
                      className="text-[#C65D47] border-[#C65D47]/40 hover:bg-[#C65D47] hover:text-white"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <Field
                      id={`rv-name-${i}`}
                      label="Name"
                      value={r.name}
                      onChange={(v) => {
                        const next = [...draft.reviews];
                        next[i] = { ...next[i], name: v };
                        update({ reviews: next });
                      }}
                    />
                    <Field
                      id={`rv-country-${i}`}
                      label="Country / city"
                      value={r.country}
                      onChange={(v) => {
                        const next = [...draft.reviews];
                        next[i] = { ...next[i], country: v };
                        update({ reviews: next });
                      }}
                    />
                    <Field
                      id={`rv-rating-${i}`}
                      label="Rating (1-5)"
                      value={String(r.rating ?? 5)}
                      type="number"
                      onChange={(v) => {
                        const next = [...draft.reviews];
                        next[i] = {
                          ...next[i],
                          rating: Math.max(1, Math.min(5, Number(v) || 5)),
                        };
                        update({ reviews: next });
                      }}
                    />
                  </div>
                  <Field
                    id={`rv-stay-${i}`}
                    label="Stay (e.g. August 2024 · Couple)"
                    value={r.stay || ""}
                    onChange={(v) => {
                      const next = [...draft.reviews];
                      next[i] = { ...next[i], stay: v };
                      update({ reviews: next });
                    }}
                  />
                  <Field
                    id={`rv-body-${i}`}
                    label="Review text"
                    value={r.body}
                    onChange={(v) => {
                      const next = [...draft.reviews];
                      next[i] = { ...next[i], body: v };
                      update({ reviews: next });
                    }}
                    multiline
                    rows={3}
                  />
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              data-testid="review-add"
              onClick={() => {
                update({
                  reviews: [
                    ...(draft.reviews || []),
                    {
                      name: "Guest name",
                      country: "City, Country",
                      rating: 5,
                      body: "Write the review here…",
                      stay: "Month Year · Group",
                    },
                  ],
                });
              }}
              className="mt-5"
            >
              <Plus size={14} className="mr-2" /> Add review
            </Button>
            <ResetButton onClick={() => resetSection("reviews")} />
          </AccordionContent>
        </AccordionItem>

        {/* ---------------- Nearby ---------------- */}
        <AccordionItem value="nearby" className="border-b border-[#EAE0D5]">
          <AccordionTrigger
            data-testid="acc-nearby"
            className="px-6 hover:no-underline"
          >
            <span className="font-serif-display text-lg text-[#232E26]">
              Location · Nearby points ({(draft.nearby || []).length})
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div className="space-y-4">
              {(draft.nearby || []).map((n, i) => (
                <div
                  key={i}
                  data-testid={`nearby-edit-${i}`}
                  className="border border-[#EAE0D5] p-5"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <p className="overline">Point #{i + 1}</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid={`nearby-delete-${i}`}
                      onClick={() => {
                        const next = [...draft.nearby];
                        next.splice(i, 1);
                        update({ nearby: next });
                      }}
                      className="text-[#C65D47] border-[#C65D47]/40 hover:bg-[#C65D47] hover:text-white"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <Field
                    id={`nb-title-${i}`}
                    label="Title"
                    value={n.title}
                    onChange={(v) => {
                      const next = [...draft.nearby];
                      next[i] = { ...next[i], title: v };
                      update({ nearby: next });
                    }}
                  />
                  <Field
                    id={`nb-body-${i}`}
                    label="Detail (distance / description)"
                    value={n.body}
                    onChange={(v) => {
                      const next = [...draft.nearby];
                      next[i] = { ...next[i], body: v };
                      update({ nearby: next });
                    }}
                  />
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              data-testid="nearby-add"
              onClick={() => {
                update({
                  nearby: [
                    ...(draft.nearby || []),
                    { title: "Place", body: "Distance / detail" },
                  ],
                });
              }}
              className="mt-5"
            >
              <Plus size={14} className="mr-2" /> Add point
            </Button>
            <ResetButton onClick={() => resetSection("nearby")} />
          </AccordionContent>
        </AccordionItem>

        {/* ---------------- House notes ---------------- */}
        <AccordionItem value="houseNotes">
          <AccordionTrigger
            data-testid="acc-house"
            className="px-6 hover:no-underline"
          >
            <span className="font-serif-display text-lg text-[#232E26]">
              Footer · House notes ({(draft.houseNotes || []).length})
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div className="space-y-3">
              {(draft.houseNotes || []).map((n, i) => (
                <div
                  key={i}
                  data-testid={`house-edit-${i}`}
                  className="flex items-center gap-3"
                >
                  <Input
                    value={n}
                    onChange={(e) => {
                      const next = [...draft.houseNotes];
                      next[i] = e.target.value;
                      update({ houseNotes: next });
                    }}
                    className={inputClass + " mt-0 flex-1"}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid={`house-delete-${i}`}
                    onClick={() => {
                      const next = [...draft.houseNotes];
                      next.splice(i, 1);
                      update({ houseNotes: next });
                    }}
                    className="text-[#C65D47] border-[#C65D47]/40 hover:bg-[#C65D47] hover:text-white"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              data-testid="house-add"
              onClick={() => {
                update({
                  houseNotes: [...(draft.houseNotes || []), "New house note"],
                });
              }}
              className="mt-5"
            >
              <Plus size={14} className="mr-2" /> Add note
            </Button>
            <ResetButton onClick={() => resetSection("houseNotes")} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="sticky bottom-4 flex justify-end">
        <Button
          data-testid="content-save-floating"
          onClick={save}
          disabled={saving || !dirty}
          className="bg-[#C65D47] hover:bg-[#A84A35] text-white shadow-lg px-6 h-12"
        >
          {saving ? (
            <Loader2 size={14} className="animate-spin mr-2" />
          ) : (
            <Save size={14} className="mr-2" />
          )}
          Save changes
        </Button>
      </div>
    </div>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  multiline = false,
  rows = 3,
  type = "text",
  help,
}) {
  return (
    <div>
      <Label className="overline" htmlFor={id}>
        {label}
      </Label>
      {multiline ? (
        <Textarea
          id={id}
          data-testid={`field-${id}`}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className={textareaClass}
        />
      ) : (
        <Input
          id={id}
          data-testid={`field-${id}`}
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
      )}
      {help && (
        <p className="text-xs text-[#5B6E4A] mt-1.5 italic">{help}</p>
      )}
    </div>
  );
}

function ResetButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-5 inline-flex items-center gap-2 text-xs text-[#5B6E4A] hover:text-[#C65D47] tracking-[0.18em] uppercase"
    >
      <RotateCcw size={12} /> Restore default text
    </button>
  );
}
